import {
  MessageCircle, Target, Mail, Calendar, BookOpen,
  Mic, FileCheck, CalendarDays, StickyNote, Paperclip,
  Contact, UserCircle,
} from '../../icons';
import { tWorkspace, type WorkspaceI18nKey } from '../../lib/workspace-i18n';

/**
 * 사용처 종류별 아이콘·이름 — **한 곳** (2026-08-19).
 *
 * 같은 표가 여태 세 군데에 복제돼 있었다(목록 배지 `WorkspaceTable` · 세부정보
 * `WorkspaceDetailPanel` · 좌측 스마트 폴더 `WorkspaceSmartFolders`). 출처가 하나 늘 때마다
 * 세 곳을 다 고쳐야 했고, 하나라도 빠뜨리면 **그 화면만 조용히 터졌다**
 * (`USAGE_META[kind]` 가 undefined → `meta.icon` 에서 렌더 예외 → 목록이 통째로 빔).
 *
 * 이제 배지 쪽 두 화면이 이 모듈 하나를 쓴다. 좌측 스마트 폴더는 이름이 더 길어
 * (`채팅 첨부` vs `채팅`) 자기 표를 갖지만, 같은 어휘와 같은 fallback 규칙을 쓴다.
 *
 * ## 새 출처가 생기면
 *
 * 서버 `workspace-usage.ts` 에 참조처를 켜면 **여기를 안 고쳐도 동작한다** —
 * `usageMetaOf()` 가 모르는 종류에 기본 아이콘과 종류 이름을 붙인다. 아래 표에 한 줄을
 * 더하는 것은 "제대로 된 아이콘과 번역을 붙이는" 일이다.
 */
export interface UsageMeta {
  icon: typeof MessageCircle;
  labelKey: WorkspaceI18nKey;
}

export const USAGE_META: Partial<Record<string, UsageMeta>> = {
  // ── 지금 서버가 세는 것 ──
  chat: { icon: MessageCircle, labelKey: 'workspace.usage.chat' },
  task: { icon: Target, labelKey: 'workspace.usage.task' },
  mail: { icon: Mail, labelKey: 'workspace.usage.mail' },
  calendar: { icon: Calendar, labelKey: 'workspace.usage.calendar' },
  board: { icon: BookOpen, labelKey: 'workspace.usage.board' },
  /*
   * 명함 사진 둘 (2026-10-07) — ⭐ 「후보」가 아니라 **서버가 이미 센다**:
   *   `workspace-usage.ts:81` `USAGE_KINDS` 에 `'contact'`·`'ecard'` 가 둘 다 있다(#2218).
   *
   * ⛔ **그래서 이 두 줄이 없는 동안 사용자는 영문 낱말을 봤다.** `usageLabelOf` 의 마지막 줄이
   *    `text === key ? kind : text` 라 **열쇠 대신 종류 이름**(`contact`·`ecard`)이 그려진다.
   *    ⇒ 「`workspace.usage.contact` 가 그대로 뜬다」가 **아니다** — 그 폴백이 한 겹 막아 준다.
   *      (그래도 한국어 화면에 영문 낱말이라 결함이다.)
   * ⇒ ★ 이 결함이 난 까닭: #2218 이 서버에서 그 둘을 **켜면서** 화면 쪽 이름을 안 챘다.
   *   ⇒ ⭐ 그래서 아래 「후보」 절의 뜻이 커졌다 — **후보 등록은 「서버가 켤 수 있는 모든 종류」로**
   *     해 두어야 한다. 실제로 `minutes` 는 미리 깔려 있어서 서버가 켜는 순간 「회의록」으로 떴고,
   *     백엔드가 `voice` 로 쓰려던 이름을 그 줄이 `minutes` 로 되돌렸다(값을 했다).
   */
  contact: { icon: Contact, labelKey: 'workspace.usage.contact' },
  ecard: { icon: UserCircle, labelKey: 'workspace.usage.ecard' },

  // ── 후보: 서버가 켜는 즉시 제대로 보이도록 미리 등록 (비용 0) ──
  minutes: { icon: Mic, labelKey: 'workspace.usage.minutes' },
  approval: { icon: FileCheck, labelKey: 'workspace.usage.approval' },
  reservation: { icon: CalendarDays, labelKey: 'workspace.usage.reservation' },
  memo: { icon: StickyNote, labelKey: 'workspace.usage.memo' },
};

/** 모르는 종류가 와도 반드시 무언가를 돌려준다 — 화면이 터지지 않게. */
export function usageMetaOf(kind: string): UsageMeta {
  return USAGE_META[kind] ?? {
    icon: Paperclip,
    labelKey: `workspace.usage.${kind}` as WorkspaceI18nKey,
  };
}

/** 화면에 쓸 이름 — 사전에 없으면 i18n 키 대신 종류 이름을 보여 준다. */
export function usageLabelOf(kind: string): string {
  const key = usageMetaOf(kind).labelKey;
  const text = tWorkspace(key);
  return text === key ? kind : text;
}
